import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { Carousel as Value } from "@reactivated";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

type IProps = Immutable<{
    value: Value;
}>;

type ISlide = NonNullable<Immutable<Value>["slides"]>[number];
type ISlideChild = NonNullable<ISlide["body"]>[number];

const renderSlideChild = (block: ISlideChild): React.JSX.Element => {
    switch (block.type) {
        case "subtitle":
            return (
                <h4 className="carousel__cell-headline" key={block.id}>
                    {block.value}
                </h4>
            );
        case "description":
            return (
                <p className="carousel__cell-descript" key={block.id}>
                    {block.value}
                </p>
            );
        case "price":
            return (
                <p className="carousel__cell-price" key={block.id}>
                    {block.value}
                </p>
            );
        case "cta":
            // The legacy carousel_cta.html hardcoded title="#"; preserved.
            return (
                <a
                    key={block.id}
                    className="carousel__cell-cta button"
                    href={getLinkUrl(block.value)}
                    title="#"
                >
                    {block.value.display_name}
                </a>
            );
    }
};

/**
 * The Carousel drives Flickity over ``.carousel__cells`` and syncs the
 * ``.carousel__tab`` buttons to the selected cell via ``activeTab`` state
 * (`active` class + `aria-current`); clicking a tab selects its cell. Flickity
 * restructures the cells subtree imperatively after mount, but the cells' JSX
 * is stable across renders, so React never reconciles into that subtree.
 *
 * - ``slide.title`` is a RawHTMLBlock, so it is injected via
 *   ``dangerouslySetInnerHTML``.
 */
export const Carousel = (props: IProps): React.JSX.Element => {
    const slides = props.value.slides ?? [];
    const cellsRef = React.useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = React.useState(0);
    const handleFlickityInit = React.useCallback(
        (flickity: Flickity): (() => void) => {
            const handleSelect = (): void => {
                setActiveTab(flickity.selectedIndex);
            };
            flickity.on("select", handleSelect);
            return () => {
                flickity.off("select", handleSelect);
            };
        },
        [],
    );
    const { flickityRef } = useFlickity(cellsRef, {
        module: "flickity",
        options: { prevNextButtons: true, pageDots: false },
        enabled: slides.length > 0,
        deps: [slides.length],
        onInit: handleFlickityInit,
    });
    return (
        <section
            className={`carousel carousel--x${slides.length}`}
            aria-label="carousel"
        >
            <div className="carousel__container">
                <h3 className="carousel__headline">{props.value.title}</h3>
                <div className="carousel__tabs">
                    {slides.map((slide, index) => (
                        <button
                            key={index}
                            className={`carousel__tab carousel__tab--cell${index + 1}${index === activeTab ? " active" : ""}`}
                            data-selector={`.cell${index + 1}`}
                            aria-current={
                                index === activeTab ? "true" : "false"
                            }
                            onClick={() => flickityRef.current?.select(index)}
                            dangerouslySetInnerHTML={{
                                __html: slide.title ?? "",
                            }}
                        />
                    ))}
                    <hr />
                </div>
                <div className="carousel__cells" ref={cellsRef}>
                    {slides.map((slide, index) => (
                        <div
                            key={index}
                            className={`carousel__cell cell${index + 1}`}
                        >
                            <div className="carousel__cell-item">
                                <figure className="carousel__cell-image">
                                    <img
                                        className="img-responsive"
                                        src={originalUrl(slide.image)}
                                        alt={
                                            slide.image?.default_alt_text ?? ""
                                        }
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </figure>
                                {slide.body && slide.body.length > 0 && (
                                    <div className="carousel__cell-copy">
                                        {slide.body.map(renderSlideChild)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
